/**
 * Protocol-level promotion: a page of protocols, one protocol opened, and the generate and apply
 * actions. A protocol is read while its generation job runs, so the screen follows the job to its end.
 */

import { useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { keepPreviousData } from '@tanstack/query-core';
import { promotionService, type ListProtocolsParams } from '@/services/promotion.service';
import type { PromotionSettingsChange, ScenarioSetting } from '@/modules/promotion/types';
import { invalidateQueries } from '@/lib/query-client';
import { isJobActive } from '@/modules/promotion/stage';

const JOB_POLL_MS = 3000;

export const promotionKey = (...parts: string[]): string[] => ['promotion', ...parts];

const protocolsQueryAtom = (params: ListProtocolsParams) =>
	atomWithQuery(() => ({
		queryKey: promotionKey('protocols', JSON.stringify(params)),
		queryFn: () => promotionService.listProtocols(params),
		staleTime: 30 * 1000,
		// Keep the previous page on screen while the next loads, so paging and typing do not flash.
		placeholderData: keepPreviousData
	}));

const protocolQueryAtom = (sopId: string) =>
	atomWithQuery(() => ({
		queryKey: promotionKey('protocol', sopId),
		queryFn: () => promotionService.getProtocol(sopId),
		enabled: Boolean(sopId),
		staleTime: 0,
		// Follow a running generation: its types settle one at a time on the job document.
		refetchInterval: (query) => (isJobActive(query.state.data?.latestJob) ? JOB_POLL_MS : false)
	}));

const generateMutationAtom = atomWithMutation(() => ({
	mutationKey: promotionKey('generate'),
	mutationFn: ({
		sopId,
		intents,
		typeIds,
		source
	}: {
		sopId: string;
		intents?: string[];
		typeIds?: string[];
		/** Generate from the tickets a person marked Correct, whatever the type's readiness. */
		source?: 'approved';
	}) => promotionService.generate(sopId, { intents, typeIds, ...(source ? { source } : {}) }),
	onSuccess: (_job, variables) => {
		invalidateQueries(promotionKey('protocol', variables.sopId));
		invalidateQueries(promotionKey('protocols'));
	}
}));

const planQueryAtom = (sopId: string) =>
	atomWithQuery(() => ({
		queryKey: promotionKey('plan', sopId),
		queryFn: () => promotionService.getPlan(sopId),
		enabled: Boolean(sopId),
		staleTime: 0
	}));

const settingsQueryAtom = atomWithQuery(() => ({
	queryKey: promotionKey('settings'),
	queryFn: () => promotionService.getSettings(),
	staleTime: 60 * 1000
}));

/** What a promotion changes: the protocol, its plan, its live types' templates, the list's rollups and the blueprints it writes to. */
const invalidateProtocol = (sopId: string): void => {
	invalidateQueries(promotionKey('protocol', sopId));
	invalidateQueries(promotionKey('live', sopId));
	invalidateQueries(promotionKey('plan', sopId));
	invalidateQueries(promotionKey('protocols'));
	invalidateQueries(['promoted-blueprints', 'workflow']);
};

const promoteMutationAtom = atomWithMutation(() => ({
	mutationKey: promotionKey('promote'),
	mutationFn: ({
		sopId,
		scenarios,
		drafts,
		acknowledgeNotReady
	}: {
		sopId: string;
		scenarios: Record<string, ScenarioSetting>;
		drafts: string[];
		acknowledgeNotReady?: string[];
	}) => promotionService.promote(sopId, scenarios, drafts, acknowledgeNotReady),
	onSuccess: (_record, variables) => invalidateProtocol(variables.sopId),
	// A refusal can mean the drafts changed since the dialog opened: reload so it shows the latest.
	onError: (_error, variables) => invalidateProtocol(variables.sopId)
}));

/** Read-only: what Promote or Stop would change on the blueprints. A mutation because the settings vary per call. */
const previewMutationAtom = atomWithMutation(() => ({
	mutationKey: promotionKey('preview'),
	mutationFn: ({
		sopId,
		scenarios,
		drafts
	}: {
		sopId: string;
		scenarios: Record<string, ScenarioSetting> | null;
		drafts?: string[];
	}) => promotionService.preview(sopId, scenarios, drafts)
}));

const stopMutationAtom = atomWithMutation(() => ({
	mutationKey: promotionKey('stop'),
	mutationFn: ({ sopId }: { sopId: string }) => promotionService.stop(sopId),
	onSuccess: (_record, variables) => invalidateProtocol(variables.sopId)
}));

const updateSettingsMutationAtom = atomWithMutation(() => ({
	mutationKey: promotionKey('settings', 'update'),
	mutationFn: (change: PromotionSettingsChange) => promotionService.updateSettings(change),
	onSuccess: () => {
		invalidateQueries(promotionKey('settings'));
		invalidateQueries(promotionKey('plan'));
		invalidateQueries(['knowledge-promotion', 'items']);
	}
}));

const applyMutationAtom = atomWithMutation(() => ({
	mutationKey: promotionKey('apply'),
	mutationFn: ({ sopId }: { sopId: string }) => promotionService.apply(sopId),
	onSuccess: (_applied, variables) => {
		invalidateQueries(promotionKey('protocol', variables.sopId));
		invalidateQueries(promotionKey('protocols'));
		invalidateQueries(['promoted-blueprints', 'workflow']);
	}
}));

export function useProtocols(params: ListProtocolsParams) {
	const key = JSON.stringify(params);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const atom = useMemo(() => protocolsQueryAtom(params), [key]);
	const [query] = useAtom(atom);
	return query;
}

export function useProtocol(sopId: string) {
	const atom = useMemo(() => protocolQueryAtom(sopId), [sopId]);
	const [query] = useAtom(atom);
	return query;
}

export function useGenerateTemplates() {
	const [mutation] = useAtom(generateMutationAtom);
	return mutation;
}

export function useApplyProtocol() {
	const [mutation] = useAtom(applyMutationAtom);
	return mutation;
}

export function usePromotionPlan(sopId: string) {
	const atom = useMemo(() => planQueryAtom(sopId), [sopId]);
	const [query] = useAtom(atom);
	return query;
}

export function usePromotionSettings() {
	const [query] = useAtom(settingsQueryAtom);
	return query;
}

export function usePromoteProtocol() {
	const [mutation] = useAtom(promoteMutationAtom);
	return mutation;
}

export function usePromotionPreview() {
	const [mutation] = useAtom(previewMutationAtom);
	return mutation;
}

export function useStopPromotion() {
	const [mutation] = useAtom(stopMutationAtom);
	return mutation;
}

export function useUpdatePromotionSettings() {
	const [mutation] = useAtom(updateSettingsMutationAtom);
	return mutation;
}
